import { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { resolveQr, resolvePhysical, activatePhysical, resolveWallet } from "@/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, CheckCircle2, Loader2, SwitchCamera, FlipHorizontal } from "lucide-react";

const REGION_ID = "cd-qr-region";
const FACING_KEY = "bs_qr_facing"; // persisted per device/browser: "environment" | "user"

const MIRROR_PREFIX = "bs_qr_mirror:"; // persisted per camera deviceId: "1" | "0"

const readFacing = () => {
  try {
    const v = localStorage.getItem(FACING_KEY);
    return v === "user" || v === "environment" ? v : "environment";
  } catch (e) {
    return "environment";
  }
};

// Explicit per-camera mirror preference (undefined = none saved yet).
const readMirrorPref = (key) => {
  try {
    const v = localStorage.getItem(MIRROR_PREFIX + key);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch (e) { /* noop */ }
  return undefined;
};

export const QrScanner = ({ open, onClose, onResolved, business, mode = "select", onReplaceScan, contextTop, contextBottom }) => {
  const [status, setStatus] = useState("starting"); // starting|scanning|detected|resolving|found|invalid|permission|free
  const [facing, setFacing] = useState(readFacing); // rear ("environment") is the default
  const [multiCam, setMultiCam] = useState(false);
  const [invalidMsg, setInvalidMsg] = useState("Dieser QR-Code gehört zu keiner gültigen Stempelkarte dieses Betriebs.");
  const [freeCard, setFreeCard] = useState(null);
  const [actName, setActName] = useState("");
  const [actAmount, setActAmount] = useState("");
  const [actBusy, setActBusy] = useState(false);
  const [mirror, setMirror] = useState(false); // single controlled preview-mirror mechanism
  const [videoAR, setVideoAR] = useState(null); // intrinsic camera aspect ratio (w/h)
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const mirrorSessionRef = useRef({}); // { [cameraKey]: bool } — session memory + explicit choices
  const cameraKeyRef = useRef("default");
  const videoElRef = useRef(null);
  const videoHandlerRef = useRef(null);

  const stop = useCallback(async () => {
    if (videoElRef.current && videoHandlerRef.current) {
      try {
        videoElRef.current.removeEventListener("loadedmetadata", videoHandlerRef.current);
        videoElRef.current.removeEventListener("resize", videoHandlerRef.current);
      } catch (e) { /* noop */ }
    }
    videoElRef.current = null;
    videoHandlerRef.current = null;
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch (e) { /* already stopped */ }
      try { await s.clear(); } catch (e) { /* noop */ }
    }
  }, []);

  const onDecoded = useCallback(async (text) => {
    if (handledRef.current) return; // one QR selects the customer only once
    handledRef.current = true;
    setStatus("detected");
    await stop();
    setStatus("resolving");
    // Replace mode: only capture a FREE card's token, never activate/select here.
    if (mode === "replace") {
      try {
        const r = await resolvePhysical(text);
        if (r.physical_status === "free") {
          setStatus("found");
          setTimeout(() => onReplaceScan && onReplaceScan({ secure_token: text, card_number: r.card_number, card_id: r.card_id }), 300);
        } else {
          setInvalidMsg("Diese Karte ist nicht frei und kann nicht als Ersatzkarte verwendet werden.");
          setStatus("invalid");
        }
      } catch (e) {
        const st = e.response?.status;
        setInvalidMsg(
          st === 403 ? (e.response?.data?.detail || "Diese Karte gehört zu einem anderen Betrieb.")
          : st === 409 ? (e.response?.data?.detail || "Diese Karte ist nicht gültig.")
          : st === 404 ? "Diese Karte konnte nicht gefunden werden."
          : "Diese Karte konnte nicht geladen werden.");
        setStatus("invalid");
      }
      return;
    }
    // Wallet passes carry an explicit "wlt_" prefixed scan_token — resolve directly and
    // never fall back to physical/digital resolution for a known Wallet token.
    if (text.startsWith("wlt_")) {
      try {
        const w = await resolveWallet(text);
        setStatus("found");
        setTimeout(() => onResolved({ customer_id: w.customer_id, name: w.name, source: "wallet" }), 400);
      } catch (e) {
        const st = e.response?.status;
        setInvalidMsg(
          st === 409 ? (e.response?.data?.detail || "Dieser Wallet-Pass ist nicht mehr gültig.")
          : st === 403 ? (e.response?.data?.detail || "Wallet ist für diesen Betrieb nicht aktiviert.")
          : "Dieser Wallet-Code gehört zu keiner gültigen Karte dieses Betriebs.");
        setStatus("invalid");
      }
      return;
    }
    // Physical cards carry a secure_token (not a customer id) — try that first.
    try {
      const r = await resolvePhysical(text);
      if (r.physical_status === "active") {
        setStatus("found");
        setTimeout(() => onResolved({ customer_id: r.customer_id, name: r.state.customer.name, source: "physical" }), 400);
      } else if (r.physical_status === "free") {
        setFreeCard({ id: r.card_id, card_number: r.card_number });
        setStatus("free");
      }
      return;
    } catch (e) {
      const st = e.response?.status;
      if (st === 403 || st === 409) {
        // wrong business / blocked / replaced / archived → show safe message, no fallback
        setInvalidMsg(e.response?.data?.detail || "Diese Karte ist nicht gültig.");
        setStatus("invalid");
        return;
      }
      // 404 unknown token → likely a digital customer QR, fall back to the existing flow.
    }
    try {
      const cust = await resolveQr(text);
      setStatus("found");
      setTimeout(() => onResolved({ customer_id: cust.customer_id, name: cust.name, source: "digital" }), 400);
    } catch (e) {
      setInvalidMsg("Dieser QR-Code gehört zu keiner gültigen Stempelkarte dieses Betriebs.");
      setStatus("invalid");
    }
  }, [stop, onResolved, mode, onReplaceScan]);

  const activateFree = async () => {
    if (!freeCard) return;
    const mode = business?.loyalty_mode || business?.loyalty?.mode || "stamp_fixed";
    const needsAmount = mode !== "stamp_fixed";
    const amount = needsAmount ? parseFloat(actAmount) : null;
    if (needsAmount && (!amount || amount <= 0)) return;
    setActBusy(true);
    try {
      const opts = { name: actName.trim() || null, action_id: `pc-${freeCard.id}-${Date.now()}` };
      if (needsAmount) opts.amount = amount; else opts.quantity = 1;
      const state = await activatePhysical(freeCard.id, opts);
      setStatus("found");
      setTimeout(() => onResolved({ customer_id: state.customer.id, name: state.customer.name, source: "physical" }), 300);
    } catch (e) {
      const st = e.response?.status;
      setInvalidMsg(st ? (e.response?.data?.detail || "Aktivierung fehlgeschlagen.") : "Keine Verbindung. Bitte Internetverbindung prüfen.");
      setStatus("invalid");
    } finally { setActBusy(false); }
  };

  const start = useCallback(async (preferred) => {
    handledRef.current = false;
    // fresh scan session → never carry a previous card's inputs / found state
    setFreeCard(null); setActName(""); setActAmount("");
    setStatus("starting");
    setVideoAR(null);
    const want = preferred || readFacing();
    const other = want === "environment" ? "user" : "environment";
    const config = { fps: 10, qrbox: { width: 240, height: 240 } };

    const attempt = async (constraint) => {
      await stop();
      const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(constraint, config, onDecoded, () => {});
    };

    try {
      let usedFacing = want;
      try {
        await attempt({ facingMode: want });
      } catch (e1) {
        // Preferred camera unavailable — fall back gracefully to the other facing…
        try {
          await attempt({ facingMode: other });
          usedFacing = other;
        } catch (e2) {
          // …and as a last resort to whatever camera the device exposes.
          const cams = await Html5Qrcode.getCameras();
          if (!cams || cams.length === 0) throw e1;
          await attempt(cams[0].id);
        }
      }
      // Determine the ACTUAL facing of the camera that opened (hardware evidence),
      // not just the requested value. Fall back to the requested/started facing.
      let actualFacing = usedFacing;
      try {
        const s = scannerRef.current?.getRunningTrackSettings?.();
        if (s && (s.facingMode === "user" || s.facingMode === "environment")) {
          actualFacing = s.facingMode;
        }
      } catch (e) { /* facingMode not exposed on this device — keep requested */ }
      setFacing(actualFacing);
      try { localStorage.setItem(FACING_KEY, actualFacing); } catch (e) { /* noop */ }

      // Resolve the preview-mirror preference for the camera that actually opened.
      // Priority: explicit session/user choice > persisted per-camera > auto default (front = mirrored).
      let camKey = "default";
      try {
        const st = scannerRef.current?.getRunningTrackSettings?.();
        if (st?.deviceId) camKey = st.deviceId;
      } catch (e) { /* deviceId not exposed — fall back to session memory */ }
      cameraKeyRef.current = camKey;
      let wantMirror;
      if (camKey in mirrorSessionRef.current) {
        wantMirror = mirrorSessionRef.current[camKey];
      } else {
        const pref = readMirrorPref(camKey);
        wantMirror = pref !== undefined ? pref : (actualFacing === "user");
      }
      setMirror(wantMirror);

      // Fit the frame to the real video aspect ratio (removes the black band from a forced square).
      const region = document.getElementById(REGION_ID);
      const v = region?.querySelector("video");
      if (v) {
        videoElRef.current = v;
        const handler = () => { if (v.videoWidth && v.videoHeight) setVideoAR(v.videoWidth / v.videoHeight); };
        videoHandlerRef.current = handler;
        v.addEventListener("loadedmetadata", handler);
        v.addEventListener("resize", handler);
        handler();
      }

      setStatus("scanning");
      // Only offer the switch control when a second camera actually exists.
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setMultiCam(devices.filter((d) => d.kind === "videoinput").length >= 2);
      } catch (e) {
        setMultiCam(false);
      }
    } catch (e) {
      setStatus("permission");
    }
  }, [onDecoded, stop]);

  useEffect(() => {
    if (open) start();
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const isScanning = ["starting", "scanning", "detected", "resolving"].includes(status);

  const close = async () => { await stop(); onClose(); };
  const retry = async () => { await stop(); start(); };
  const switchCamera = async () => {
    const next = facing === "environment" ? "user" : "environment";
    try { localStorage.setItem(FACING_KEY, next); } catch (e) { /* noop */ }
    setFacing(next);
    await start(next);
  };
  const toggleMirror = () => {
    const camKey = cameraKeyRef.current;
    const next = !mirror;
    setMirror(next);
    mirrorSessionRef.current[camKey] = next; // remember for this session (all cameras)
    if (camKey !== "default") {
      try { localStorage.setItem(MIRROR_PREFIX + camKey, next ? "1" : "0"); } catch (e) { /* noop */ }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="qr-scanner-overlay">
      <div className="flex items-center justify-between p-4 text-white">
        <p className="font-display font-extrabold text-lg">QR-Code scannen</p>
        <button data-testid="qr-scanner-close" onClick={close} className="p-2 rounded-full bg-white/10 press" aria-label="Schließen">
          <X className="h-5 w-5" />
        </button>
      </div>
      {contextTop && <div data-testid="qr-context-top" className="px-6 pt-1 pb-2">{contextTop}</div>}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-6">
        {/* Display-only preview mirror — ONE controlled mechanism, driven solely by the
            "Vorschau spiegeln" toggle. Scoped strictly to the injected <video>; does not
            affect decoding, overlays or controls. The video is fitted to its real aspect
            ratio (via the container) so it fills the frame without stretching or cropping. */}
        <style>{`
          #${REGION_ID} video { width: 100% !important; height: 100% !important; object-fit: contain !important; }
          #${REGION_ID}.bs-cam-mirror video { transform: scaleX(-1) !important; }
        `}</style>
        <div id={REGION_ID} style={{ aspectRatio: videoAR || 1 }} className={`w-full max-w-xs rounded-3xl overflow-hidden bg-black ring-2 ring-white/20 ${mirror ? "bs-cam-mirror" : ""} ${isScanning ? "" : "hidden"}`} />
        {status === "scanning" && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {multiCam && (
              <Button data-testid="qr-switch-camera-button" onClick={switchCamera}
                className="rounded-xl font-bold press bg-white/15 hover:bg-white/25 text-white border border-white/20">
                <SwitchCamera className="mr-2 h-4 w-4" />
                {facing === "environment" ? "Kamera wechseln (Frontkamera)" : "Kamera wechseln (Rückkamera)"}
              </Button>
            )}
            <Button data-testid="qr-mirror-toggle" onClick={toggleMirror} aria-pressed={mirror}
              className={`rounded-xl font-bold press border ${mirror ? "bg-white text-slate-900 border-white hover:bg-white/90" : "bg-white/15 hover:bg-white/25 text-white border-white/20"}`}>
              <FlipHorizontal className="mr-2 h-4 w-4" />
              Vorschau spiegeln · {mirror ? "An" : "Aus"}
            </Button>
          </div>
        )}
        <div className="mt-6 text-center text-white min-h-[52px]" data-testid="qr-scanner-status">
          {status === "starting" && <p className="flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Kamera wird gestartet …</p>}
          {status === "scanning" && <p className="text-white/80 text-sm">Richte die Kamera auf den QR-Code der Stempelkarte.</p>}
          {status === "detected" && <p className="flex items-center gap-2 justify-center font-bold" style={{ color: "var(--brand-secondary, #F39200)" }}><CheckCircle2 className="h-5 w-5" /> QR-Code erkannt</p>}
          {status === "resolving" && <p className="flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Kunde wird gesucht …</p>}
          {status === "found" && <p className="flex items-center gap-2 justify-center text-[#16A34A] font-bold"><CheckCircle2 className="h-5 w-5" /> Kunde gefunden</p>}
          {status === "free" && (
            <div className="space-y-3 w-full max-w-xs relative z-10">
              <p className="text-white/80 text-sm">Neue Karte {freeCard?.card_number}. Optional Name & Einkauf erfassen.</p>
              <Input data-testid="phys-activate-name" value={actName} onChange={(e) => setActName(e.target.value)} placeholder="Name hinzufügen (optional)" className="h-11 rounded-xl bg-white text-slate-900" />
              {(business?.loyalty_mode || business?.loyalty?.mode || "stamp_fixed") !== "stamp_fixed" && (
                <Input data-testid="phys-activate-amount" type="number" inputMode="decimal" min="0" step="0.01" value={actAmount} onChange={(e) => setActAmount(e.target.value)} placeholder="Einkaufsbetrag (€)" className="h-11 rounded-xl bg-white text-slate-900" />
              )}
              <Button data-testid="phys-activate-button" onClick={activateFree} disabled={actBusy} className="w-full rounded-xl font-bold press text-white" style={{ background: "var(--brand-primary, #E1251B)" }}>
                {actBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Aktivieren &amp; buchen
              </Button>
            </div>
          )}
          {status === "invalid" && (
            <div className="space-y-3">
              <p data-testid="qr-invalid-message" className="text-white font-semibold px-2">{invalidMsg}</p>
              <Button data-testid="qr-retry-button" onClick={retry} className="rounded-xl font-bold press text-white" style={{ background: "var(--brand-primary, #E1251B)" }}>Erneut versuchen</Button>
            </div>
          )}
          {status === "permission" && (
            <div className="space-y-3">
              <p data-testid="qr-permission-message" className="text-white font-semibold px-2">Kamerazugriff wurde nicht erlaubt. Bitte aktiviere den Kamerazugriff in deinen Browser-Einstellungen.</p>
              <Button data-testid="qr-retry-button" onClick={retry} className="rounded-xl font-bold press text-white" style={{ background: "var(--brand-primary, #E1251B)" }}>Erneut versuchen</Button>
            </div>
          )}
        </div>
        {contextBottom && <div className="mt-2 flex justify-center">{contextBottom}</div>}
      </div>
    </div>
  );
};
